import React from 'react';
import {
  Loading,
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import SelectDiffField from '../utils/auditTracks/SelectDiffField';
import { reportTypesDisplayChoices } from '../../constants/reportTypes';
import { reportStatusesChoices } from '../../constants/reportStatuses';

type IReportAudit = {
  type: string;
  status: string;
  createdAt: string;
  errorCode: string;
};

const ReportAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IReportAudit>>();
  const { data, isLoading } = useGetOne<AuditTrack<IReportAudit>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IReportAudit>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    type: resourceData.type?.hasChanged,
    status: resourceData.status?.hasChanged,
    createdAt: resourceData.createdAt?.hasChanged,
    errorCode: resourceData.errorCode?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && !record.extraInfo && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.type && (
            <SelectDiffField source="type" label="Type" choices={reportTypesDisplayChoices} />
          )}
          {hasFieldChanges.status && (
            <SelectDiffField source="status" label="Status" choices={reportStatusesChoices} />
          )}
          {hasFieldChanges.createdAt && <DateDiffField source="createdAt" label="Created at" showTime />}
          {hasFieldChanges.errorCode && <TextDiffField source="errorCode" label="Error code" />}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default ReportAuditDetails;
